import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import BrandHeader from '../components/BrandHeader';
import { getBrandConfig, getCurrentBrand } from '../brand-config';
import { Deliverable, getHolyShippedDetail, WeekDetail as HolyShippedWeekDetail } from '../data/holyShipped';
import '../styles/holy-shipped.css';

interface WeekDetailProps {
  weekNumber: number;
  onBack: () => void;
}

const WeekDetail: React.FC<WeekDetailProps> = ({ weekNumber, onBack }) => {
  const navigate = useNavigate();
  const brandConfig = getBrandConfig();
  const currentBrand = useMemo(() => getCurrentBrand(), []);
  const reportRef = useRef<HTMLDivElement>(null);
  const [showAllDeliverables, setShowAllDeliverables] = useState(false);
  const currentWeek = useMemo<HolyShippedWeekDetail | undefined>(
    () => getHolyShippedDetail(currentBrand, weekNumber),
    [currentBrand, weekNumber],
  );

  if (!currentWeek) {
    return (
      <div
        className="app holy-shipped-app"
        style={{ '--primary-color': brandConfig.primaryColor } as React.CSSProperties}
      >
        <BrandHeader
          actionLinks={[
            { label: '← Holy Shipped', onClick: onBack },
          ]}
        />
        <div className="dashboard">
          <main className="dashboard-main">
            <div className="dashboard-container" style={{ textAlign: 'center', padding: '4rem 0' }}>
              <h2>Week not found</h2>
              <p style={{ marginTop: '1rem' }}>
                The selected Holy Shipped report is unavailable for {brandConfig.name} right now.
              </p>
              <button onClick={onBack} className="hs-back-btn" style={{ marginTop: '2rem' }}>
                ← Back to All Weeks
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const brandToken = currentBrand === 'marsmen' ? 'mars-men' : currentBrand === 'hollow' ? 'hollow' : null;

  const deliverableMatchesBrand = (deliverable: Deliverable) => {
    if (!deliverable.brands || deliverable.brands.length === 0) {
      return true;
    }

    const brandSet = new Set(deliverable.brands);
    if (brandSet.has('all') || brandSet.has('es')) {
      return true;
    }

    return brandToken !== null && brandSet.has(brandToken);
  };

  const deliverablesForBrand = useMemo(
    () => currentWeek.deliverables.filter(deliverableMatchesBrand),
    [currentWeek, brandToken]
  );

  const deliverablesToShow = showAllDeliverables ? currentWeek.deliverables : deliverablesForBrand;

  const totalDeliverables = currentWeek.deliverables.length;
  const brandDeliverableCount = deliverablesForBrand.length;
  const completedBrandDeliverables = deliverablesForBrand.filter(d => d.status === 'complete').length;

  const getBrandBadges = (brands?: ('mars-men' | 'hollow' | 'es' | 'all')[]) => {
    if (!brands) return null;

    const labelForBrand = (brand: 'mars-men' | 'hollow' | 'es' | 'all') => {
      switch (brand) {
        case 'mars-men':
          return 'Mars Men';
        case 'hollow':
          return 'Hollow Socks';
        case 'es':
          return 'Easy Street';
        case 'all':
        default:
          return 'All Brands';
      }
    };

    return (
      <div className="hs-brand-badges">
        {brands.map((brand) => (
          <span key={brand} className={`hs-brand-badge ${brand}`}>
            {labelForBrand(brand)}
          </span>
        ))}
      </div>
    );
  };

  const exportToPNG = async () => {
    const element = reportRef.current;
    if (!element) {
      return;
    }

    try {
      const canvas = await html2canvas(element, {
        height: element.scrollHeight,
        width: element.scrollWidth,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f5f7fa',
      });

      const link = document.createElement('a');
      link.download = `holy-shipped-week-${weekNumber}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/holy-shipped/week/${weekNumber}`;

    try {
      await navigator.clipboard.writeText(url);
      alert(`Link copied: ${url}`);
    } catch (error) {
      console.error('Failed to copy week link:', error);
      alert('Unable to copy link automatically. Please copy it manually.');
    }
  };

  const brandCompletionRate = brandDeliverableCount === 0
    ? 0
    : Math.round((completedBrandDeliverables / brandDeliverableCount) * 100);

  return (
    <div
      className="app holy-shipped-app"
      style={{ '--primary-color': brandConfig.primaryColor } as React.CSSProperties}
    >
      <BrandHeader
        actionLinks={[
          { label: '← Holy Shipped', onClick: onBack },
        ]}
      />
      <div className="dashboard" ref={reportRef} id="holy-shipped-dashboard">
        <div className="hs-stats-header">
          <div className="dashboard-container">
            <div className="hs-stats-grid">
              <div className="hs-stat-card">
                <span className="hs-stat-value">{brandDeliverableCount}</span>
                <span className="hs-stat-label">{brandConfig.name} Deliverables</span>
              </div>
              <div className="hs-stat-card">
                <span className="hs-stat-value">{brandCompletionRate}%</span>
                <span className="hs-stat-label">Brand Completion</span>
              </div>
              <div className="hs-stat-card">
                <span className="hs-stat-value">{totalDeliverables}</span>
                <span className="hs-stat-label">Total Shipped</span>
              </div>
            </div>
          </div>
        </div>

        <main className="dashboard-main">
        <div className="dashboard-container">
          <div className="hs-back-button brand-detail-header">
            <button onClick={onBack} className="hs-back-btn">
              ← Back to All Weeks
            </button>
            <div className="hs-back-controls">
              <button
                onClick={() => setShowAllDeliverables(prev => !prev)}
                className="hs-back-btn"
              >
                {showAllDeliverables ? 'Show Brand Only' : 'Include Cross-Brand'}
              </button>
              <button onClick={handleCopyLink} className="hs-back-btn">
                📋 Copy Week Link
              </button>
              <button onClick={exportToPNG} className="hs-back-btn">
                📸 Export Week
              </button>
            </div>
          </div>

          <div className="hs-week-header">
            <div className="hs-week-info">
              <h2 className="hs-week-title">This Week's Accomplishments</h2>
              <p className="hs-week-date">Week of {currentWeek.weekOf}</p>
            </div>
          <div className="hs-week-stats">
            <div className="hs-week-stat">
              <span className="hs-stat-number">{brandDeliverableCount}</span>
              <span className="hs-stat-label">{brandConfig.name}</span>
            </div>
            <div className="hs-week-stat">
              <span className="hs-stat-number">{totalDeliverables}</span>
              <span className="hs-stat-label">All Brands</span>
            </div>
            <div className="hs-week-stat">
              <span className="hs-stat-number">{brandCompletionRate}%</span>
              <span className="hs-stat-label">Complete</span>
            </div>
          </div>
        </div>

        {/* Business Value Summary */}
          <div className="hs-summary-card">
            <h3 className="hs-card-title">Business Value Delivered</h3>
            {currentWeek.businessValue ? (
              <div className="hs-summary-text">
                {currentWeek.businessValue.map((value, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>• {value}</div>
                ))}
              </div>
            ) : (
              <p className="hs-summary-text">{currentWeek.executiveSummary}</p>
            )}
          </div>

        {/* Technical Foundation */}
        <div className="hs-summary-card">
          <h3 className="hs-card-title">Technical Foundation</h3>
          <p className="hs-summary-text">
            {weekNumber === 7
              ? "Week 7 was a creative automation sprint: the new Gemini studio and custom interface let non-designers remix assets instantly, Frame.io automations run QC without manual reviews, AI review search feeds creative briefs in seconds, and SES infrastructure keeps workflows ready to email across both brands."
              : currentWeek.executiveSummary}
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="hs-two-column">
          {/* Deliverables */}
          <div className="hs-deliverables-card">
            <h3 className="hs-card-title">Deliverables</h3>
            <div className="hs-deliverables-list">
                  {deliverablesToShow.map((deliverable, index) => (
                <div key={index} className="hs-deliverable">
                  <div className="hs-deliverable-content">
                    <div className="hs-deliverable-header">
                      <div className="hs-deliverable-left">
                        <span className={`hs-status-indicator ${deliverable.status}`} />
                        <span className="hs-deliverable-name">{deliverable.name}</span>
                        {deliverable.link && (
                          <span className="hs-deliverable-link" title={deliverable.link}>🔗</span>
                        )}
                      </div>
                      <div className="hs-deliverable-right">
                        <div className="hs-progress-bar">
                          <div 
                            className="hs-progress-fill"
                            style={{ width: `${deliverable.completion}%` }}
                          />
                        </div>
                        <span className="hs-completion">{deliverable.completion}%</span>
                      </div>
                    </div>
                    {deliverable.brands && getBrandBadges(deliverable.brands)}
                    {deliverable.description && (
                      <div className="hs-deliverable-details">
                        <p className="hs-deliverable-description">{deliverable.description}</p>
                        {deliverable.impact && (
                          <p className="hs-deliverable-impact"><strong>Why it matters:</strong> {deliverable.impact}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status & Priorities */}
          <div className="hs-sidebar">
            <div className="hs-status-card">
              <h3 className="hs-card-title">Current Status</h3>
              <div className="hs-status-items">
                <div className="hs-status-item">
                  <span className="hs-status-label">Brand</span>
                  <span className="hs-status-value">{brandConfig.name}</span>
                </div>
                <div className="hs-status-item">
                  <span className="hs-status-label">Brand Deliverables</span>
                  <span className="hs-status-value">{brandDeliverableCount}</span>
                </div>
                <div className="hs-status-item">
                  <span className="hs-status-label">Completion</span>
                  <span className="hs-status-value">{brandCompletionRate}%</span>
                </div>
                <div className="hs-status-item">
                  <span className="hs-status-label">Total Deliverables</span>
                  <span className="hs-status-value">{totalDeliverables}</span>
                </div>
              </div>
            </div>

            <div className="hs-priorities-card">
              <h3 className="hs-card-title">Next Week Priorities</h3>
              <ul className="hs-priority-list">
                {currentWeek.nextWeekPriorities.map((priority, index) => (
                  <li key={index} className="hs-priority-item">
                    <span className="hs-priority-number">{index + 1}</span>
                    <span className="hs-priority-text">{priority}</span>
                  </li>
                ))}
              </ul>
            </div>

            {currentWeek.blockers && currentWeek.blockers.length > 0 && (
              <div className="hs-blockers-card">
                <h3 className="hs-card-title hs-blockers-title">⚠️ Blockers</h3>
                <ul className="hs-blockers-list">
                  {currentWeek.blockers.map((blocker, index) => (
                    <li key={index} className="hs-blocker-item">{blocker}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        </div>
      </main>

        <footer className="dashboard-footer">
          <div className="footer-content">
            <p>© 2024 {brandConfig.name}. Internal automation workspace.</p>
            <div className="footer-links">
              <a href="https://automations.easystreetbrands.com" target="_blank" rel="noopener noreferrer">
                Automation Portal
              </a>
              <span className="separator">•</span>
              <a
                href="/"
                onClick={(event) => {
                  event.preventDefault();
                  navigate('/');
                }}
              >
                Brand Home
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default WeekDetail;
